import { createReducer } from "@reduxjs/toolkit";
import {
  dSalesCheckedAllItemAC,
  dSalesCheckedItemAC,
  dSalesClearFiltersAC,
  dSalesSetDataAC,
  dSalesSetDetailItemAC,
  dSalesSetDetailItemFromRefundAC,
  dSalesSetFiltersAC,
  dSalesSetItemsForPageAC,
  dSalesSetPageNumberAC,
  dSalesSetPaginationAC,
} from "model/dashboardSales/dSalesActionCreators";
import { typePagination } from "../system/constants";
import { IdRefundsStateDataItem } from "model/dashboardRefunds/dRefundsReducer";
import {logoutAC, resetReducers} from "model/system/systemActionCreators";

export interface typePaymentInfoDto {
  authCode: string;
  merchantId: string;
  paymentSystemName: string;
  responseCode: string;
  rrn: string;
  stan: string;
  terminalId: string;
  transactionNumber: number;
}

export type typeSlimSalesItem = {
  checked: boolean;
  id: string;
  saleNumber: string | null;
  date: string;
  userId: string;
  userName: string;
  salePublicId: string;
  appletVersion: string;
  status: "SALE" | "CREDIT" | "ADVANCE";
  paid: boolean;
  refunded: boolean;
  payments: {
    id: string;
    paymentType: saleTypes;
    date: string;
    paymentAmount: number;
    paymentTransactionValue: number | null;
    paymentInfo?: {
      paymentSystemName?: string;
    };
    paymentInfoDto?: {
      paymentSystemName?: string;
    };
    method: saleTypes;
    transactionNumber: string | null;
    amount: number;
  }[];
  amount: number;
  discountAmount: number;
  servicePaymentAmount: number;
  fiscalSign: string;
  receiptNumber: string;
  store: {
    id: string;
    name: string;
    address: string;
    phoneNumber: string;
    email: string;
    manager: string;
    managerPhoneNumber: string;
    description: string;
    industry: string | null;
    deleted: boolean;
    interfaceType: string | null;
  };
};

export type typeInitialProduct = {
  vat: number;
  active: boolean;
  barcodes: string[];
  category: {
    deleted: boolean;
    id: string;
    name: string;
  };
  units: string;
  deleted: boolean;
  id: string;
  integerUnit: boolean;
  marked: boolean;
  name: string;
  psid: string;
  commissionTIN: string;
  commissionPINFL: string;
  stores: {
    active: true;
    id: string;
    price: number;
    store: {
      address: string;
      deleted: true;
      description: string;
      email: string;
      id: string;
      industry: {
        id: string;
        name: string;
      };
      manager: string;
      managerPhoneNumber: string;
      name: string;
      phoneNumber: string;
    }[];
    units: string;
    vat: number;
  };
};

export type typeFullSalesItem = {
  id: string;
  date: string;
  amount: number;
  cashierId: string;
  discountAmount: number;
  payments: {
    id: string;
    date: string;
    amount: number;
    paymentAmount: number;
    paymentInfoDto: typePaymentInfoDto | null;
    paymentType: string;
    paymentInfo?: {
      paymentSystemName?: string;
    };
  }[];
  pos: {
    dateCreated: string;
    dateEdited: string;
    fiscalCardId: string;
    id: string;
    posHardwareModel: string;
    posHardwareSerialNumber: string;
    posNumber: number;
    store: {
      address: string;
      deleted: boolean;
      description: string;
      email: string;
      id: string;
      industry: {
        id: string;
        name: string;
      };
      manager: string;
      managerPhoneNumber: string;
      name: string;
      phoneNumber: string;
    };
  };
  productList: {
    id: string;
    amount: number;
    marked: boolean;
    markedLabels: string[];
    price: number;
    customTotal: number | null;
    tax: number | null;
    productName: string;
    unitCode: number | null;
    packageCode: number | null;
    commissionPINFL: string | null;
    commissionTIN: string | null;
    barcodes: string[];
    psid: string;
    productOwnerType: string | null;
    discountAmount: number;
    product: typeInitialProduct;
  }[];
  refunds: IdRefundsStateDataItem[];
  refunded: boolean;
  saleNumber: number;
  salePublicId: string;
  servicePaymentAmount: number;
  store: {
    address: string;
    deleted: true;
    description: string;
    email: string;
    id: string;
    industry: {
      id: string;
      name: string;
    };
    manager: string;
    managerPhoneNumber: string;
    name: string;
    phoneNumber: string;
  };
  userId: string;
  userName: string;
  appletVersion: string;
  fiscalSign: string;
  fmId: string;
  receiptNumber: string;
  paid: boolean;
  status: "SALE" | "CREDIT" | "ADVANCE";
  orderId: string | null;
  zreportNumber: string;
};

export enum saleTypes {
  "CASH" = "CASH",
  "CARD" = "CARD",
  "CASHLESS" = "CASHLESS",
  "QR" = "QR",
}

export type typeSalesFilters = {
  receiptNumber?: string | null;
  saleId?: string | null;
  salePaymentType?: string | null;
  date?: {
    from: string | null;
    to: string | null;
  } | null;
  cashierName?: string | null;
  storeName?: string | null;
  storeId?: string | null;
  paymentStatus?: string | null;
};
export type IdSalesState = {
  pagination: typePagination;
  data: typeSlimSalesItem[];
  detailItem: typeSlimSalesItem | undefined;
  filters: typeSalesFilters;
};

// Init state
export const initialState: IdSalesState = {
  pagination: {
    pageNumber: 0,
    itemsPerPage: 14,
  },
  data: [],
  detailItem: undefined,
  filters: {
    date: null,
    storeName: null,
    cashierName: null,
    storeId: null,
  },
};

// Reducer
export const DSalesReducer = createReducer(initialState, (builder) => {
  builder

      // Reducer of set data
      .addCase(dSalesSetDataAC, (state, action) => {
        state.data = action.payload.dataList;
      })

      // Reducer of set pagination
      .addCase(dSalesSetPaginationAC, (state, action) => {
        state.pagination = {...state.pagination, ...action.payload.pagination};
      })

      // Reducer of set items for page pagination
      .addCase(dSalesSetItemsForPageAC, (state, action) => {
        state.pagination = {
          ...state.pagination,
          pageNumber: 0,
          itemsPerPage: action.payload.itemsForPage,
        };
      })

      // Reducer of set pageNumber pagination
      .addCase(dSalesSetPageNumberAC, (state, action) => {
        state.pagination.pageNumber = action.payload.pageNumber;
      })

      // Reducer of set detail item
      .addCase(dSalesSetDetailItemAC, (state, action) => {
        state.detailItem = state.data.find((item) => item.id === action.payload);
      })

      // Reducer of set detail item
      .addCase(dSalesSetDetailItemFromRefundAC, (state, action) => {
        state.detailItem = action.payload;
      })

      // Reducer of clear filtersForm
      .addCase(dSalesClearFiltersAC, (state) => {
        state.filters.date = null;
        state.filters.cashierName = null;
        state.filters.storeName = null;
        state.filters.storeId = null;
      })

      // Reducer of set filtersForm
      .addCase(dSalesSetFiltersAC, (state, action) => {
        state.filters = {...state.filters, ...action.payload};
        state.pagination.pageNumber = 0;
      })
      // Reducer of set checked item
      .addCase(dSalesCheckedItemAC, (state, action) => {
        state.data = state.data.map((item) => {
          if (item.id === action.payload) {
            const newItem = item;
            newItem.checked = !item.checked;
            return newItem;
          } else {
            return item;
          }
        });
      })

      // Reducer of set checked all items
      .addCase(dSalesCheckedAllItemAC, (state, action) => {
        state.data = state.data.map((item) => {
          const newItem = item;
          newItem.checked = action.payload;
          return newItem;
        });
      })
      // Reducer of clear state
      .addCase(logoutAC, (state) => {
        state.data = initialState.data;
        state.detailItem = initialState.detailItem;
        state.filters = initialState.filters;
        state.pagination = initialState.pagination;
      })

      .addCase(resetReducers, (state, action) => {
        if (action.payload !== "sales") {
          state.data = initialState.data;
          state.detailItem = initialState.detailItem;
          state.filters = initialState.filters;
          state.pagination = initialState.pagination;
        }
      })
})
